import { v4 as uuid } from 'uuid';
import { useQuery } from 'react-query';

import { responseWithData } from 'utils';
import { useUserToken } from 'auth';

import { isAdminUser } from 'auth/token';

import useInfiniteUASQuery from 'hooks/useInfiniteUASQuery';

import { UASParams, UASResult } from 'utils/UASTypes';

import { api } from '.';

import {
	Offer,
	OfferDto,
	FileRef,
	OfferHomepageDto,
	OfferLimitedDto,
} from 'typing/endpoints';

// Offer

export const useOffer = (id: string) =>
	useQuery(['offers', id], (_key: string, id: string) =>
		api().get(`offers/${id}`).json<Offer>(),
	);

export const useOfferDto = (id: string) =>
	useQuery(['offers/dto', id], (_key: string, id: string) =>
		api().get(`offers/${id}/dto`).json<OfferDto>(),
	);

export const useLimitedOfferDto = (id: string) =>
	useQuery(['offers', id, 'limited'], (_key: string, id: string) =>
		api({ skipToken: true })
			.get(`offers/${id}/limited`)
			.json<OfferLimitedDto>(),
	);

// Offers

const getOffers = (json: UASParams) =>
	api().post(`offers/parametrized`, { json }).json<UASResult<Offer>>();

const getOfferDtos = (json: UASParams) =>
	api().post('offers/dto', { json }).json<UASResult<OfferDto>>();

export const useOffers = (json: UASParams) => {
	const token = useUserToken();
	const useDto = !isAdminUser(token);
	return useInfiniteUASQuery<Offer | OfferDto, [UASParams, boolean]>(
		['offers', json, useDto],
		(_key, params, useDto, page = 0) =>
			useDto
				? getOfferDtos({ ...params, page })
				: getOffers({ ...params, page }),
	);
};

export const useOffersPaginated = (json: UASParams) => {
	const token = useUserToken();
	const useDto = !isAdminUser(token);
	return useInfiniteUASQuery<Offer | OfferDto, [UASParams, boolean]>(
		['offers', json, useDto],
		(_key, params, useDto) =>
			useDto ? getOfferDtos({ ...params }) : getOffers({ ...params }),
	);
};

//Public offers on homepage
export const useOffersPublic = () =>
	useQuery('offers/active-for-homepage/', () =>
		api({ skipToken: true })
			.get('offers/active-for-homepage/')
			.json<OfferHomepageDto[]>(),
	);

// Edit
export const saveOffer = (
	offer: Partial<Omit<OfferDto, 'id'>>,
	id: string = uuid(),
) =>
	responseWithData<OfferDto>(
		api().put(`offers/${id}/save`, {
			json: { ...offer, id },
		}),
	);

// Activation

export const offersActivate = async (id: string) =>
	(await api().put(`offers/${id}/activate`, { throwHttpErrors: false }))?.ok;

export const offersDeactivate = async (id: string) =>
	(await api().put(`offers/${id}/deactivate`, { throwHttpErrors: false })).ok;

// Export
export const exportOffers = (params: UASParams) =>
	api()
		.post(`offers/export`, { json: { ...params, pageSize: -1 } })
		.json<FileRef>();

// Other

export const useOffersForLoggedInCompany = () =>
	useInfiniteUASQuery<Offer>(
		['offers', 'logged-in-company'],
		(_key, _json, page = 0) =>
			api()
				.post('offers/logged-in-company', {
					json: { page },
				})
				.json<UASResult<Offer>>(),
		{ staleTime: 36000000 },
	);

export const useAllOffersForLoggedInCompany = () =>
	useQuery(
		['offers', 'logged-in-company', 'all'],
		() =>
			api()
				.post('offers/logged-in-company', {
					json: { pageSize: -1 },
				})
				.json<UASResult<Offer>>(),
		{ refetchOnWindowFocus: true, refetchOnMount: true },
	);

export const useSuggestedOffersOfStudent = () =>
	useQuery('offers/student-recommended', () =>
		api().get('offers/student-recommended').json<OfferDto[]>(),
	);

export const addToFavourites = async (offerId: string) =>
	(
		await api().put(`offers/${offerId}/add-favorite`, {
			throwHttpErrors: false,
		})
	)?.ok;

export const removeFromFavourites = async (offerId: string) =>
	(
		await api().delete(`offers/${offerId}/remove-favorite`, {
			throwHttpErrors: false,
		})
	)?.ok;

export const useFavouriteOffers = () =>
	useQuery(['fav-offers'], () =>
		api().get('offers/favorite-offers-of-logged-in-student').json<OfferDto[]>(),
	);
